import React, { useEffect, useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import { ConfirmationDetails, ConfirmationDecision } from '../../service/ordisApiService';
import './ConfirmationCard.css';

interface ConfirmationCardProps {
  token: string;
  details: ConfirmationDetails;
  onRespond: (token: string, decision: ConfirmationDecision) => void;
}

/** Whole seconds until an ISO timestamp, floored at zero. */
function secondsUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 1000)) : 0;
}

/**
 * Human-In-The-Loop confirmation card for a high-risk gated action.
 *
 * Renders the server-verified action details (not the agent's restated text),
 * a live expiry countdown, and Approve/Deny buttons. Once the human responds —
 * or the token expires — the buttons are disabled so a decision is effectively
 * single-use from the UI side too (the server enforces single-use
 * authoritatively via the token store).
 */
const ConfirmationCard: React.FC<ConfirmationCardProps> = ({ token, details, onRespond }) => {
  const [responded, setResponded] = useState<ConfirmationDecision | null>(null);
  const [remaining, setRemaining] = useState<number>(() => secondsUntil(details.expiresAt));

  useEffect(() => {
    if (responded) return undefined;
    const timer = setInterval(() => {
      setRemaining(secondsUntil(details.expiresAt));
    }, 1000);
    return () => clearInterval(timer);
  }, [details.expiresAt, responded]);

  const expired = remaining <= 0;
  const disabled = responded !== null || expired;

  const handle = (decision: ConfirmationDecision) => {
    if (disabled) return;
    setResponded(decision);
    onRespond(token, decision);
  };

  const mmss = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;

  return (
    <Box
      className={`confirmation-card ${details.isProduction ? 'confirmation-card--prod' : ''}`}
      data-testid="confirmation-card"
    >
      <Box className="confirmation-card__header">
        <span className="confirmation-card__icon" aria-hidden="true">
          🔒
        </span>
        <span className="confirmation-card__title">Confirmation required</span>
      </Box>

      <Box className="confirmation-card__body">
        <div className="confirmation-card__action">{details.action}</div>
        {Object.entries(details.parameters).map(([key, value]) => (
          <div key={key} className="confirmation-card__param">
            <span className="confirmation-card__param-key">{key}</span>
            <span className="confirmation-card__param-val">{String(value)}</span>
          </div>
        ))}
        {details.isProduction && (
          <div className="confirmation-card__prod-warning">
            ⚠ This targets a PRODUCTION resource.
          </div>
        )}
      </Box>

      <Box className="confirmation-card__footer">
        {responded === null && !expired && (
          <>
            <span className="confirmation-card__timer" aria-label="time remaining">
              Expires in {mmss}
            </span>
            <span className="confirmation-card__buttons">
              <Button
                type="primary"
                size="small"
                onClick={() => handle('approve')}
                disabled={disabled}
              >
                Approve
              </Button>
              <Button
                type="secondary"
                size="small"
                onClick={() => handle('deny')}
                disabled={disabled}
              >
                Deny
              </Button>
            </span>
          </>
        )}
        {responded === 'approve' && (
          <span className="confirmation-card__status">Approved ✓</span>
        )}
        {responded === 'deny' && (
          <span className="confirmation-card__status">Denied ✗</span>
        )}
        {responded === null && expired && (
          <span className="confirmation-card__status confirmation-card__status--expired">
            Expired — please make the request again.
          </span>
        )}
      </Box>
    </Box>
  );
};

export default ConfirmationCard;
